import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch } from '@/hooks/dispatch'
import { actionCreators } from '@/store/OrganizationalUnits'
import { useReduxState } from '@/hooks/reduxState'
import { DropDownList } from '@/components/dropDownList'
import LoadingIndicator from '@/components/LoadingIndicator'
import { useLocalization } from '@/hooks/localization'

export const OrganizationalUnitSelector = React.memo(function OrganizationalUnitSelector (props) {
  const { onChange, value } = props
  const [selectedUnits, setSelectedUnits] = useState([])
  const { localizeProps } = useLocalization()
  const { units, treeData, loading } = useReduxState(useCallback(
    state => ({
      units: state.organizationalUnits.units,
      treeData: state.organizationalUnits.treeData,
      loading: state.organizationalUnits.loading
    })
    , []))
  const [getAll] = useDispatch([actionCreators.getAll])
  const maxSearchLevel = 10

  useEffect(() => {
    getAll()
  }, [getAll])

  const findSelectedUnits = useCallback((val) => {
    let unitsTree = []
    if (val) {
      unitsTree.unshift(val)
      const finalUnit = units.find(x => x.id === val)
      let parentUnit = units.find(x => x.organizationUnitId === finalUnit.organizationUnitParentId)
      if (parentUnit) {
        let parentValue = parentUnit.id
        unitsTree.unshift(parentValue)
        let i = 1
        while (parentUnit.organizationUnitParentId !== null) {
          if (i === maxSearchLevel) {
            break
          }

          parentUnit = units.find(x => x.id === parentValue)

          if (parentUnit && parentUnit.organizationUnitParentId) {
            parentValue = units.find(x => x.organizationUnitId === parentUnit.organizationUnitParentId).id
            unitsTree.unshift(parentValue)
          }

          i++
        }
      }
    }
    return unitsTree
  }, [units, maxSearchLevel])

  if (!loading || treeData.length !== 0) {
    if (value && units && units.length > 0 && selectedUnits.length === 0) {
      const unitsTree = findSelectedUnits(value)
      setSelectedUnits(unitsTree)
    }
  }

  const onOrganizationalUnitChange = useCallback(({ val, currentDepth }) => {
    const newSelectedUnits = (!val ? selectedUnits.slice(0, currentDepth) : findSelectedUnits(val))
    const newValue = !val
      ? newSelectedUnits.length > 0 ? newSelectedUnits[newSelectedUnits.length - 1] : null
      : val
    setSelectedUnits(newSelectedUnits)
    onChange({ value: newValue })
  }, [onChange, selectedUnits, findSelectedUnits])

  function renderDropDowns (units) {
    let dropDowns = []
    units.forEach((unit, index) => {
      dropDowns.push(renderNode(treeData, units, 0, index))
    })

    dropDowns.push(renderNode(treeData, units, 0, units.length))

    return dropDowns
  }

  function renderNode (node, units, currentDepth, desiredDepth) {
    if (!node || node.length === 0) {
      return
    }

    if (currentDepth !== desiredDepth) {
      if (units[currentDepth]) {
        return renderNode((node.find(x => x.id === units[currentDepth]) || {}).children, units, currentDepth + 1, desiredDepth)
      } else {
        return renderNode((node.find(x => x.id === units[currentDepth - 1]) || {}).children, units, currentDepth + 1, desiredDepth)
      }
    }

    const dropDownValue = units[currentDepth] ? units[currentDepth] : null
    return <DropDownList
      key={`organizationalUnitsSelector_unit_unit${currentDepth}_${dropDownValue}`}
      dataSource={node}
      onChange={({ value }) => onOrganizationalUnitChange({ val: value, currentDepth })}
      textField={localizeProps()}
      valueField='id'
      value={dropDownValue}
      clientSide
      allowClear />
  }

  return (<React.Fragment>
    {loading || treeData.length === 0
      ? <LoadingIndicator />
      : renderDropDowns(selectedUnits)
    }
  </React.Fragment>)
})



// WEBPACK FOOTER //
// ./src/components/organizationalUnitSelector.js